import { AppThunk } from '../../../store/reducers';
import { DELETE, GET, POST, PUT } from '../../../service/HttpMethod';
import {
	InitialState,
	Details,
	ActionConstant,
	ActionType,
	SubscriptionDetails,
	PlanDetails,
} from './types';
import { toastFunction } from '../../../helpers/toastShow';

const pd: PlanDetails = {
	id: 0,
	name: '',
	documentCount: 0,
	documentSize: 0,
	scanCount: 0,
	projectCount: 0,
	tradeCount: 0,
	userCount: 0,
	duration: 0,
	durationUnit: 0,
	amount: 0,
	createdBy: 0,
	organizationId: 0,
	isActive: true,
	isDeleted: false,
	packageKey: '',
	priceKey: '',
};
const sd: SubscriptionDetails = {
	id: 0,
	name: '',
	userId: 0,
	planId: 0,
	organizationId: 0,
	paymentMethod: '',
	expiryDate: '',
	documentCount: 0,
	documentSize: 0,
	scanCount: 0,
	projectCount: 0,
	tradeCount: 0,
	userCount: 0,
	duration: 0,
	durationUnit: 0,
	amount: 0,
	isActive: true,
	isDeleted: false,
	documentCountRemains: 0,
	documentSizeRemains: 0,
	scanCountRemains: 0,
	projectCountRemains: 0,
	tradeCountRemains: 0,
	userCountRemains: 0,
	documentCountTotal: 0,
	documentSizeTotal: 0,
	scanCountTotal: 0,
	projectCountTotal: 0,
	tradeCountTotal: 0,
	userCountTotal: 0,
};
const d: Details = {
	id: 0,
	name: '',
	location: '',
	email: '',
	phone: '',
	primaryContact: '',
	isDeleted: false,
	createdBy: 0,
	subscriptionId: 0,
	enterprisePlanId: 0,
	Plan: pd,
	Subscription: sd,
};
const initialState: InitialState = {
	detailsFetching: true,
	details: d,
	statusCode: 0,
};

const GetDetailsAction = (data: Details): ActionType => ({
	type: ActionConstant.GET_DETAILS,
	data,
});
export const clearDetails = (): ActionType => ({ type: ActionConstant.CLEAR_DETAILS });
export const clearStatus = (): ActionType => ({ type: ActionConstant.CLEAR_STATUS });

export const getDetails = (id: number): AppThunk => {
	// organization details
	const getConfig = { url: `organizations/${id}` };
	return async (dispatch) => {
		try {
			const { data = {} } = await GET(getConfig);
			dispatch(GetDetailsAction(data));
		} catch (error) {
			if (error && error.data && error.data.message) {
				dispatch(toastFunction('Error', error.data.message, ''));
			} else {
				dispatch(toastFunction('Error', 'Something went wrong', ''));
			}
		}
	};
};

export function create(datas: any = {}): AppThunk {
	const postConfig = {
		data: datas,
		url: `plan`,
	};
	return async (dispatch) => {
		try {
			const { status = 0 } = await POST(postConfig);
			dispatch({ type: ActionConstant.GET_STATUS, status });
			dispatch(clearDetails());
			dispatch(clearStatus());
			dispatch(getDetails(datas.organizationId));
			dispatch(toastFunction('Success', 'Plan created successfully', ''));
		} catch (error) {
			dispatch({ type: ActionConstant.GET_STATUS, status: 400 });
			dispatch(toastFunction('Error', error.data.message, ''));
		}
	};
}

export function update(datas: any = {}, planId: number, organizationId: number): AppThunk {
	const putConfig = {
		data: datas,
		url: `plan/${planId}`,
	};
	return async (dispatch) => {
		try {
			const { status = 0 } = await PUT(putConfig);
			dispatch({ type: ActionConstant.GET_STATUS, status });
			dispatch(clearDetails());
			dispatch(clearStatus());
			dispatch(getDetails(organizationId));
			dispatch(toastFunction('Success', 'Plan updated successfully', ''));
		} catch (error) {
			dispatch(toastFunction('Error', error.data.message, ''));
			dispatch({ type: ActionConstant.GET_STATUS, status: 400 });
		}
	};
}

export function deleteListItem(id = 0, organizationId = 0): AppThunk {
	const deleteConfig = { url: `plan/${id}` };
	return async function (dispatch) {
		try {
			const { status = 0, data = {} } = await DELETE(deleteConfig);
			if (data.message === 'PLAN_SUBSCRIPTION_EXISTS') {
				dispatch(
					toastFunction(
						'Error',
						'Unable to delete this plan. Organizations have already subscribed to this plan.',
						'',
					),
				);
			} else {
				dispatch({ type: ActionConstant.GET_STATUS, status });
				dispatch(clearDetails());
				dispatch(getDetails(organizationId));
				dispatch(toastFunction('Success', 'Plan deleted successfully', ''));
			}
		} catch (error) {
			dispatch({ type: ActionConstant.GET_STATUS, status: 0 });
			dispatch(toastFunction('Error', 'Plan deletion failed', ''));
		}
	};
}

export function mapTenantId(datas: any = {}): AppThunk {
	let data = {};

	if (datas.idpType === 'microsoft') {
		data = {
			tenantId: datas.tenantId,
			idpType: datas.idpType,
		};
	} else if (datas.idpType === 'okta') {
		data = {
			issuerUrl: datas.issuerUrl,
			clientId: datas.clientId,
			idpType: datas.idpType,
		};
	}

	const postConfig = {
		data,
		url: `/organizations/mapTenant`,
	};
	return async (dispatch) => {
		try {
			await POST(postConfig);
			dispatch(toastFunction('Success', 'Tenant Id mapped successfully', ''));
		} catch (error) {
			dispatch(toastFunction('Error', error.data.message, ''));
		}
	};
}

export function domainDiscovery(params: any = {}): AppThunk {
	const { idpType, email } = params;
	const getCofig = {
		url: `/organizations/domainDiscovery/${idpType}/${email}`,
	};
	return async (dispatch) => {
		try {
			const response = await GET(getCofig);
			// eslint-disable-next-line no-console
			console.info('Discover success. Navigating to Okta widget');
			return response.data;
		} catch (error) {
			dispatch(toastFunction('Error', error.data.message, ''));
		}
	};
}

function organizationReducer(state = initialState, action: ActionType): InitialState {
	switch (action.type) {
		case ActionConstant.GET_DETAILS: {
			const { data } = action;
			return {
				...state,
				details: data,
				detailsFetching: false,
			};
		}
		case ActionConstant.GET_STATUS: {
			return {
				...state,
				statusCode: action.status,
			};
		}
		case ActionConstant.CLEAR_DETAILS: {
			return {
				...state,
				details: d,
				detailsFetching: true,
			};
		}
		case ActionConstant.CLEAR_STATUS: {
			return {
				...state,
				statusCode: 0,
			};
		}
		default:
			return state;
	}
}

export default organizationReducer;
